import {
    WC_2026_SEASON_ID,
    WC_2026_STAGE_ID,
    WC_2026_GOLDEN_BOOT_STANDINGS_ID,
    WC_2026_GROUP_STANDINGS_ID,
    WC_2026_GROUP_IDS,
    SPORT_ID_FOOTBALL,
} from './statscoreIds.js';
import { cachedFetchJson, getCached, setCached, TTL_LONG, TTL_MED, TTL_SHORT, TTL_1H, TTL_2H, TTL_3H, TTL_7D, TTL_PERMANENT } from './cache.js';

const CLIENT_ID = '2720';
const SECRET_KEY = (typeof process !== 'undefined' && process.env ? process.env.STATSCORE_SECRET_KEY : null) || 'SA2q8mZYWFLEZL2QAf6tx7y2MaDeLyyBih3';
//for local run used || condetional for getting secret key from env var. will be updated later.
let cachedToken = null;
let tokenExpiration = 0;

export async function getStatscoreToken() {
    const now = Math.floor(Date.now() / 1000);
    if (cachedToken && now < tokenExpiration - 60) {
        return cachedToken;
    }

    const url = `https://api.statscore.com/v2/oauth?client_id=${CLIENT_ID}&secret_key=${SECRET_KEY}`;
    try {
        const response = await fetch(url);
        const data = await response.json();

        if (data.api?.data?.token) {
            cachedToken = data.api.data.token;
            const exp = data.api.data.token_expiration || 3600;
            // Handle both unix timestamp and seconds-to-live formats
            tokenExpiration = exp > 1000000000 ? exp : now + exp;
            return cachedToken;
        } else {
            console.error('Statscore OAuth failed:', data);
        }
    } catch (e) {
        console.error('Error fetching Statscore token:', e);
    }

    return cachedToken; // Return whatever we have (even if expired) or null if it's the first time and failed
}

export async function fetchParticipantData(playerId) {
    const token = await getStatscoreToken();
    const url = `https://api.statscore.com/v2/participants/${playerId}?token=${token}`;
    const data = await cachedFetchJson(url, TTL_LONG);
    return data.api?.data?.participant || null;
}

export async function fetchPlayerPositionStats(playerId, eventId = '5993471') {
    const token = await getStatscoreToken();

    // 1. Fetch from individual participant API (Season/Profile stats)
    const pUrl = `https://api.statscore.com/v2/participants/${playerId}?token=${token}`;
    const pData = await cachedFetchJson(pUrl, TTL_LONG);
    const participant = pData.api?.data?.participant;
    const pStats = participant?.statistics || [];

    // 2. Fetch from event participants API (Match-specific stats like Minutes Played)
    const eUrl = `https://api.statscore.com/v2/events/${eventId}/participants?token=${token}`;
    const eData = await cachedFetchJson(eUrl, TTL_1H);
    const eParticipants = eData.api?.data?.participants || [];
    let eStats = [];

    // Find player in event subparticipants
    for (const team of eParticipants) {
        const player = team.subparticipants?.find(p => String(p.id) === String(playerId));
        if (player) {
            eStats = player.statistics || [];
            break;
        }
    }

    // Helper to find a stat value from either source
    const getVal = (id) => {
        // Try event stats first (more specific), then fallback to profile stats
        const s = eStats.find(stat => String(stat.id) === String(id)) ||
            pStats.find(stat => String(stat.id) === String(id));
        return s ? s.value : 0;
    };

    return {
        // Position Stats
        xg: getVal(1470),
        chancesCreated: getVal(1480),
        shotsOnTarget: getVal(20),
        touchesInBox: getVal(940),
        passAccuracy: getVal(1166),
        successfulDribbles: getVal(1475),
        interceptions: getVal(487),
        tacklesWon: getVal(32),
        totalTackles: getVal(31),
        aerialDuelsWon: getVal(33),
        totalAerialDuels: getVal(1163),
        clearances: getVal(1395),
        cleanSheets: getVal(1162),
        shotsSaved: getVal(29),
        importantSaves: getVal(1482),
        spectacularSaves: getVal(1483),
        rating: getVal(1519) || getVal(1518),

        // Volume Metrics (Updated IDs)
        appearances: (getVal(452) || 0) + (getVal(36) || 0),
        starts: getVal(36),
        minutesPlayed: getVal(34),
        goals: getVal(40),
        assists: getVal(41)
    };
}

export const ALL_SEASON_IDS = [
    65679, // Premier League
    65813, // Championship
    65828, // League One
    65831, // League Two
    65720, // Scottish Premiership
    65991, // Champions League
    65992, // Europa League
    65993, // Europa Conference League
    65839, // Ligue 1
    65833, // Bundesliga
    65497, // Serie A
    65913, // La Liga
    65689, // Eredivisie
    68411, // FIFA World Cup
];

export async function fetchPlayerSeasonStats(
    playerId,
    seasonId = '65679',
    allSeasons = false,
    ttl = TTL_1H,
) {
    let keyPrefix;
    if (allSeasons && typeof allSeasons === 'object' && !Array.isArray(allSeasons)) {
        ttl = allSeasons.ttl ?? ttl;
        keyPrefix = allSeasons.keyPrefix;
        allSeasons = false;
    }
    const token = await getStatscoreToken();
    let ratingSum = 0;
    let ratingCount = 0;
    try {
        let mergedStats = {};
        let parentParticipant = null;

        const seasonIds = Array.isArray(allSeasons)
            ? allSeasons
            : (allSeasons === true ? ALL_SEASON_IDS : [seasonId]);

        const responses = await Promise.all(
            seasonIds.map(async (sid) => {
                try {
                    const url =
                        `https://api.statscore.com/v2/seasons/${sid}/participants-stats` +
                        `?token=${token}&participant_id=${playerId}&type=person`;

                    const data = await cachedFetchJson(url, ttl, { keyPrefix });

                    return (
                        data.api?.data?.participants_stats?.[0] ||
                        null
                    );
                } catch {
                    return null;
                }
            })
        );

        for (const participant of responses.filter(Boolean)) {
            if (!parentParticipant && participant.parent_participant) {
                parentParticipant = participant.parent_participant;
            }

            for (const stat of (participant.stats || [])) {
                const statId = String(stat.id);

                const value =
                    typeof stat.value === 'number'
                        ? stat.value
                        : parseFloat(stat.value) || 0;

                mergedStats[statId] =
                    (mergedStats[statId] || 0) + value;
                
                if (statId === '1519') {
                    ratingSum += value;
                    ratingCount++;
                };
            }
        }

        const getVal = (id) => mergedStats[String(id)] || 0;
        const averageRating =
            ratingCount > 0
                ? (ratingSum / ratingCount).toFixed(1)
                : '0.0';
                
        return {
            // Core Volume
            appearances: (getVal(1556) || 0) + (getVal(36) || 0),
            starts: getVal(36),
            minutesPlayed: getVal(34),
            goals: getVal(40),
            assists: getVal(41),

            // Forwards
            xg: getVal(1470),
            chancesCreated: getVal(1480),
            shotsOnTarget: getVal(20),
            touchesInBox: getVal(940),

            // Midfielders
            passAccuracy: getVal(1166),
            successfulDribbles: getVal(1475),
            interceptions: getVal(487),

            // Defenders
            tacklesWon: getVal(32),
            totalTackles: getVal(31),
            aerialDuelsWon: getVal(33),
            totalAerialDuels: getVal(1163),
            clearances: getVal(1395),

            // Goalkeepers
            cleanSheets: getVal(1164),
            shotsSaved: getVal(29),
            importantSaves: getVal(1482),
            spectacularSaves: getVal(1483),

            // Performance
            rating: averageRating,

            parent_participant: parentParticipant
        };
    } catch (err) {
        console.error('Error fetching season stats:', err);
        return null;
    }
}
export async function fetchPlayerTrend(playerId, playerName = null) {
    const cacheKey = `player_trend_${playerId || ''}_${playerName || ''}`;
    const cached = getCached(cacheKey);
    if (cached !== null) return cached;

    const token = await getStatscoreToken();
    if (!token) return [];

    try {
        // Helper to extract events from ALL nested structures
        const extractEventsRecursive = (obj, allEvents = []) => {
            if (!obj || typeof obj !== 'object') return allEvents;
            if (Array.isArray(obj.events)) allEvents.push(...obj.events);
            for (const key in obj) {
                if (key !== 'events' && typeof obj[key] === 'object') {
                    extractEventsRecursive(obj[key], allEvents);
                }
            }
            return allEvents;
        };

        const getEvents = async (id) => {
            const url = `https://api.statscore.com/v2/events?participant_id=${id}&limit=10&sort_type=start_date&sort_order=desc&status_type=finished&sport_id=5&token=${token}`;
            const data = await cachedFetchJson(url, TTL_MED);

            let events = data.api?.data?.events || data.api?.data?.results || [];
            if (events.length === 0 && data.api?.data) {
                events = extractEventsRecursive(data.api.data);
            }

            return events
                .sort((a, b) => new Date(b.start_date) - new Date(a.start_date))
                .slice(0, 10);
        };

        let events = await getEvents(playerId);

        // Name search fallback
        if (events.length === 0 && playerName) {
            const searchNames = [playerName];
            if (playerName.includes(' ')) searchNames.push(playerName.split(' ').pop());

            for (const nameToTry of searchNames) {
                const searchUrl = `https://api.statscore.com/v2/participants?token=${token}&name=${encodeURIComponent(nameToTry)}&type=person`;
                const sData = await cachedFetchJson(searchUrl, TTL_LONG);
                const participants = sData.api?.data?.participants || [];

                if (participants.length > 0) {
                    const foundId = participants[0].id;
                    events = await getEvents(foundId);
                    if (events.length > 0) {
                        playerId = foundId;
                        break;
                    }
                }
            }
        }

        if (events.length === 0) return [];

        const ratingPromises = events.map(async (event) => {
            try {
                const eventId = event.id;
                const subParticipantsUrl = `https://api.statscore.com/v2/events/${eventId}/sub-participants?token=${token}`;
                const eData = await cachedFetchJson(subParticipantsUrl, TTL_MED);

                const subParticipants = eData.api?.data?.sub_participants || eData.api?.data?.participants || [];
                const playerEntry = subParticipants.find(p =>
                    String(p.id) === String(playerId) ||
                    String(p.participant_id) === String(playerId) ||
                    String(p.player_id) === String(playerId) ||
                    String(p.participant?.id) === String(playerId)
                );

                if (playerEntry) {
                    const statsArray = playerEntry.statistics || playerEntry.stats || [];
                    const ratingStat = statsArray.find(s => String(s.id) === '1518');
                    return ratingStat ? parseFloat(ratingStat.value) : 0;
                }
                return 0;
            } catch (err) {
                return 0;
            }
        });

        const ratings = await Promise.all(ratingPromises);
        const finalRatings = ratings.reverse(); // Return in chronological order
        if (finalRatings.length > 0) {
            setCached(cacheKey, finalRatings, TTL_1H);
        }
        return finalRatings;
    } catch (err) {
        console.error('Trend Fetch Failed:', err);
        return [];
    }
}

export async function fetchPlayerSeasons(playerId) {
    const token = await getStatscoreToken();
    if (!token) return [];
    try {
        const url = `https://api.statscore.com/v2/participants/${playerId}/seasons?token=${token}`;
        const data = await cachedFetchJson(url, TTL_LONG);
        const seasonIds = [];
        const competitions = data.api?.data?.competitions || [];
        for (const comp of competitions) {
            if (comp.seasons) {
                for (const s of comp.seasons) {
                    if (s.id) seasonIds.push(Number(s.id));
                }
            }
        }
        return seasonIds;
    } catch (err) {
        console.error('Error fetching player seasons:', err);
        return [];
    }
}

export async function searchPlayerByName(name) {
    const token = await getStatscoreToken();
    try {
        // 1. Try full name search
        let url = `https://api.statscore.com/v2/participants?token=${token}&name=${encodeURIComponent(name)}&type=person`;
        let data = await cachedFetchJson(url, TTL_LONG);
        let participants = data.api?.data?.participants || [];

        // 2. If nothing found, try searching only the surname
        if (participants.length === 0 && name.includes(' ')) {
            const surname = name.split(' ').pop();
            url = `https://api.statscore.com/v2/participants?token=${token}&name=${encodeURIComponent(surname)}&type=person`;
            data = await cachedFetchJson(url, TTL_LONG);
            participants = data.api?.data?.participants || [];
        }

        // Return the best match
        return participants[0] || null;
    } catch (err) {
        console.error('Error searching player:', err);
        return null;
    }
}

export async function fetchTopPerformersBySeason(seasonId, positionName) {
    const cacheKey = `top_performers_${seasonId}_${String(positionName || '').toLowerCase()}`;
    const cached = getCached(cacheKey);
    if (cached !== null) return cached;

    const token = await getStatscoreToken();
    let allParticipants = [];
    let nextUrl = `https://api.statscore.com/v2/seasons/${seasonId}/participants-stats?token=${token}&type=person`;

    try {
        // 1. Exhaustive pagination loop
        while (nextUrl) {
            const data = await cachedFetchJson(nextUrl, TTL_MED);
            const pageStats = data.api?.data?.participants_stats || [];
            allParticipants = [...allParticipants, ...pageStats];
            nextUrl = data.api?.method?.next_page || null;
            if (allParticipants.length > 2000) break;
        }

        // 2. Filter for Peers based on analytical signatures for each position
        const pos = positionName.toLowerCase();
        const filteredPeers = allParticipants.filter(p => {
            const stats = p.stats || [];
            const hasStat = (id) => stats.some(s => String(s.id) === String(id) && parseFloat(s.value) > 0);

            if (pos.includes('goal')) {
                return hasStat(29) || hasStat(1164); // Saves or Clean Sheets
            } else if (pos.includes('defen')) {
                return hasStat(30) || hasStat(32); // Clearances or Tackles Won
            } else if (pos.includes('mid')) {
                return hasStat(1166) || hasStat(1475); // Pass Accuracy or Dribbles Won
            } else if (pos.includes('forw') || pos.includes('striker')) {
                return hasStat(1470) || hasStat(858); // xG or Touches in Box
            }
            return true; // Fallback
        });

        // 3. Map, sort by Average Rating (ID 1519), and take top 3
        const top3 = filteredPeers
            .map(p => {
                const ratingStat = p.stats?.find(s => String(s.id) === '1519');
                return {
                    id: p.id,
                    name: p.name,
                    rating: ratingStat ? parseFloat(ratingStat.value) : 0
                };
            })
            .sort((a, b) => b.rating - a.rating)
            .slice(0, 3);

        setCached(cacheKey, top3, TTL_2H);
        return top3;
    } catch (err) {
        console.error('Error in fetchTopPerformersBySeason:', err);
        return [];
    }
}

export async function fetchPlayerLastMatches(playerId, limit = 10) {
    const token = await getStatscoreToken();
    const url = "https://api.statscore.com/v2/events?participant_id=" + playerId + "&limit=" + limit + "&sort_type=start_date&sort_order=desc&status_type=finished&token=" + token;

    try {
        const data = await cachedFetchJson(url, TTL_MED);
        const events = data.api?.data?.events || [];

        return events.map(event => ({
            id: event.id,
            name: event.name,
            date: event.start_date,
            status: event.status
        }));
    } catch (error) {
        console.error('Error fetching player matches:', error);
        return [];
    }
}

export async function fetchEventPlayerRating(eventId, playerId) {
    const token = await getStatscoreToken();
    const url = "https://api.statscore.com/v2/events/" + eventId + "/participants?token=" + token;

    try {
        const data = await cachedFetchJson(url, TTL_MED);
        const participants = data.api?.data?.participants || [];

        for (const team of participants) {
            const player = team.subparticipants?.find(p => String(p.id) === String(playerId));
            if (player) {
                const ratingStat = player.statistics?.find(s => String(s.id) === '1519' || String(s.id) === '1518');
                return ratingStat ? parseFloat(ratingStat.value) : 0;
            }
        }
        return 0;
    } catch (error) {
        return 0;
    }
}

export async function getSeasonClubAggregatedStats(participantId, seasonId, bypassCache = false) {
    const token = await getStatscoreToken();
    if (!token) return { errors: 0, interceptions: 0 };

    try {
        // Step 1 — Get player-level season stats for all persons in the team in 1 request
        const playersUrl = `https://api.statscore.com/v2/seasons/${seasonId}/participants-stats?token=${token}&parent_participant_id=${participantId}&type=person&limit=100`;
        const playersData = await cachedFetchJson(playersUrl, TTL_MED, { force: bypassCache });

        const participantsStats = playersData.api?.data?.participants_stats || [];

        // Sum up interceptions (ID 487) and errors (ID 1473) across all players
        let errors = 0;
        let interceptions = 0;

        for (const player of participantsStats) {
            const stats = player.stats || [];

            const errorStat = stats.find(s => String(s.id) === '1473');
            if (errorStat && errorStat.value) {
                errors += parseInt(errorStat.value) || 0;
            }

            const interceptionStat = stats.find(s => String(s.id) === '487');
            if (interceptionStat && interceptionStat.value) {
                interceptions += parseInt(interceptionStat.value) || 0;
            }
        }

        // Step 2 — Fetch team-wide season statistics to get dynamic Expected Goals (xG)
        let teamXg = 0;
        try {
            const teamStatsData = await cachedFetchJson(
                `https://api.statscore.com/v2/seasons/${seasonId}/participants-stats?token=${token}&participant_id=${participantId}`,
                TTL_MED,
                { force: bypassCache }
            );
            const teamStats = teamStatsData.api?.data?.participants_stats?.[0]?.stats || [];
            const xgStat = teamStats.find(s => String(s.id) === '1470');
            if (xgStat && xgStat.value) {
                teamXg = parseFloat(xgStat.value) || 0;
            }
        } catch (e) {
            console.error('Failed to fetch team xG:', e);
        }

        return { errors, interceptions, xg: teamXg };
    } catch (err) {
        console.error('Error fetching season club stats:', err);
        return { errors: 0, interceptions: 0, xg: 0 };
    }
}

export async function getSeasonErrorsLeadingToGoals(participantId, seasonId, bypassCache = false) {
    const stats = await getSeasonClubAggregatedStats(participantId, seasonId, bypassCache);
    return stats.errors;
}

export async function fetchClubTechnicalData(seasonId, participantId, bypassCache = false) {
    const token = await getStatscoreToken();

    try {
        // Step 2: Fetch Standings List
        const listUrl = `https://api.statscore.com/v2/standings?season_id=${seasonId}&token=${token}`;
        const listData = await cachedFetchJson(listUrl, TTL_SHORT, { force: bypassCache });

        const standingsList = listData.api?.data?.standings_list || listData.api?.data?.standings || [];

        const overallStanding = standingsList.find(s =>
            (s.type_name && s.type_name.toLowerCase() === 'league standings') ||
            (s.name && s.name.toLowerCase().includes('league standings') && !s.name.toLowerCase().includes('live'))
        );

        const formStanding = standingsList.find(s =>
            (s.type_name && s.type_name.toLowerCase().includes('form table')) ||
            (s.name && s.name.toLowerCase().includes('form table'))
        );

        if (!overallStanding) {
            return { rank: 'N/A', streak: [], neighbors: [], stats: { xg: 0, goals: 0, interceptions: 0, errorsLeadingToGoals: 0 } };
        }

        const standingId = overallStanding.id;
        const formId = formStanding?.id;

        // Step 3: Fetch Detailed Standing Data (Overall)
        const detailUrl = `https://api.statscore.com/v2/standings/${standingId}?token=${token}`;
        const detailData = await cachedFetchJson(detailUrl, TTL_SHORT, { force: bypassCache });

        const apiData = detailData.api?.data || {};
        const standingsData = apiData.standings || {};
        const groups = standingsData.groups || apiData.groups || [];

        let technicalData = {
            participantId,
            seasonId,
            standingId,
            leagueName: overallStanding.name?.includes(' - ') ? overallStanding.name.split(' - ')[1] : 'N/A',
            rank: 'N/A',
            points: 0,
            streak: [],
            neighbors: [],
            stats: { xg: 0, goals: 0, interceptions: 0, errorsLeadingToGoals: 0 }
        };

        // Step 4: Extract Rank and Stats from Overall Table
        for (const group of groups) {
            const participants = group.participants || group.standings || [];
            const team = participants.find(p => String(p.id) === String(participantId) || String(p.participant_id) === String(participantId));

            if (team) {
                technicalData.rank = team.rank || team.position || 'N/A';
                technicalData.points = team.points || 0; // Fallback

                if (team.columns) {
                    const ptsCol = team.columns.find(c => c.code === 'pts' || String(c.name).toLowerCase() === 'points');
                    if (ptsCol) technicalData.points = ptsCol.value;

                    const goalsCol = team.columns.find(c => c.code === 'goals_scored');
                    if (goalsCol) technicalData.stats.goals = goalsCol.value;

                    const xgCol = team.columns.find(c => c.code === 'xg' || String(c.name).toLowerCase() === 'expected goals');
                    if (xgCol) technicalData.stats.xg = xgCol.value;
                }

                // Map Neighbors
                const teamIndex = participants.indexOf(team);
                const start = Math.max(0, teamIndex - 1);
                const end = Math.min(participants.length, teamIndex + 2);
                technicalData.neighbors = participants.slice(start, end).map(p => {
                    let pts = p.points || 0;
                    if (p.columns) {
                        const ptsCol = p.columns.find(c => c.code === 'pts' || String(c.name).toLowerCase() === 'points');
                        if (ptsCol) pts = ptsCol.value;
                    }
                    return {
                        id: p.participant_id || p.id,
                        rank: p.rank || p.position,
                        name: p.name || 'Unknown',
                        slug: p.participant_slug ? p.participant_slug.split(',')[0] : null,
                        acronym: p.acronym || p.participant_acronym || null,
                        points: pts,
                        isCurrent: String(p.id) === String(participantId) || String(p.participant_id) === String(participantId)
                    };
                });

                // Map Top 5
                technicalData.topTeams = participants.slice(0, 5).map(p => ({
                    rank: p.rank || p.position,
                    name: p.name || 'Unknown',
                    points: p.points || 0,
                    isCurrent: String(p.id) === String(participantId)
                }));

                break;
            }
        }

        // Step 5: Fetch Form Table for the 10-match streak
        if (formId) {
            const fUrl = `https://api.statscore.com/v2/standings/${formId}?token=${token}`;
            const fData = await cachedFetchJson(fUrl, TTL_SHORT, { force: bypassCache });
            const fGroups = fData.api?.data?.standings?.groups || fData.api?.data?.groups || [];

            for (const g of fGroups) {
                const parts = g.participants || g.standings || [];
                const t = parts.find(p => String(p.id) === String(participantId) || String(p.participant_id) === String(participantId));

                if (t && t.columns) {
                    const formCol = t.columns.find(c => c.code === 'form' || c.name?.toLowerCase().includes('form'));
                    if (formCol?.value) {
                        const rawStreak = formCol.value.split(',').map(s => s.trim());
                        // Take the LAST 10 results
                        technicalData.streak = rawStreak.slice(-10).map(s =>
                            s.startsWith('W') ? 'W' : (s.startsWith('L') ? 'L' : 'D')
                        );
                    }
                }
            }
        }

        // Fetch dynamic season stats
        const aggregatedStats = await getSeasonClubAggregatedStats(participantId, seasonId, bypassCache);
        technicalData.stats.errorsLeadingToGoals = aggregatedStats.errors;
        technicalData.stats.interceptions = aggregatedStats.interceptions;
        technicalData.stats.xg = aggregatedStats.xg || 0;

        return technicalData;
    } catch (error) {
        return { rank: 'N/A', streak: [], neighbors: [], stats: { xg: 0, goals: 0, interceptions: 0, errorsLeadingToGoals: 0 } };
    }
}

export async function getClubTopPlayers(seasonId, participantId, bypassCache = false) {
    const token = await getStatscoreToken();
    if (!token) return [];

    try {
        const url = `https://api.statscore.com/v2/seasons/${seasonId}/participants-stats?token=${token}&parent_participant_id=${participantId}&type=person&limit=100`;
        const data = await cachedFetchJson(url, TTL_MED, { force: bypassCache });

        const participantsStats = data.api?.data?.participants_stats || [];

        const mappedStats = participantsStats.map(p => {
            const stats = p.stats || [];
            const getVal = (statId) => {
                const s = stats.find(stat => String(stat.id) === String(statId));
                return s ? parseFloat(s.value) : 0;
            };

            return {
                id: p.id,
                name: p.name,
                position: p.details?.position_name || 'Athlete',
                minutesPlayed: getVal(34),
                goals: getVal(40),
                xg: getVal(1470),
                assists: getVal(41)
            };
        });

        // Filter players who have played at least 1 minute of first-team football in the season
        const qualifiedStats = mappedStats.filter(p => p.minutesPlayed >= 1);

        const getPositionOrder = (pos) => {
            if (!pos) return 5;
            const p = pos.toLowerCase();
            if (p.includes('goalkeeper')) return 1;
            if (p.includes('defender')) return 2;
            if (p.includes('midfielder')) return 3;
            if (p.includes('attacker') || p.includes('forward')) return 4;
            return 5;
        };

        // Sort by position (Goalkeeper -> Defender -> Midfielder -> Forward), then by minutes played descending
        const sorted = qualifiedStats.sort((a, b) => {
            const orderA = getPositionOrder(a.position);
            const orderB = getPositionOrder(b.position);
            if (orderA !== orderB) return orderA - orderB;
            return b.minutesPlayed - a.minutesPlayed;
        });

        return sorted;
    } catch (error) {
        console.error('Error fetching club top players:', error);
        return [];
    }
}

export async function fetchWorldCupGroupStandings() {
    try {
        const token = await getStatscoreToken();
        if (!token) {
            throw new Error('Failed to retrieve Statscore token');
        }

        // 1. Fetch standings list dynamically for World Cup 2026
        const listData = await cachedFetchJson(`https://api.statscore.com/v2/standings?token=${token}&season_id=${WC_2026_SEASON_ID}`, TTL_PERMANENT);
        const standingsList = listData?.api?.data?.standings_list || [];
        const groupStageStanding = standingsList.find(
            item => item.object_name === 'Group Stage' && item.type_name === 'League standings'
        );

        if (!groupStageStanding) {
            throw new Error('Group Stage standing not found in standings list');
        }

        const groupStageId = groupStageStanding.id;

        // 2. Fetch the detailed standings for the resolved group stage ID
        const result = await cachedFetchJson(`https://api.statscore.com/v2/standings/${groupStageId}?token=${token}`, TTL_PERMANENT);
        if (result?.api?.data?.standings?.groups) {
            return result.api.data.standings.groups;
        } else {
            throw new Error('Invalid API structure');
        }
    } catch (error) {
        console.error('Error fetching World Cup group standings:', error);
        throw error;
    }
}

export async function fetchWorldCupBracket() {
    try {
        const token = await getStatscoreToken();
        if (!token) {
            throw new Error('Failed to retrieve Statscore token');
        }

        const result = await cachedFetchJson(`https://api.statscore.com/v2/stages/${WC_2026_STAGE_ID}/bracket?token=${token}`, TTL_3H);
        if (result?.api?.data?.rounds) {
            return result.api.data.rounds;
        } else {
            throw new Error('Invalid bracket API structure');
        }
    } catch (error) {
        console.error('Error fetching World Cup bracket:', error);
        throw error;
    }
}

export async function fetchWorldCupTeams() {
    try {
        const token = await getStatscoreToken();
        if (!token) {
            throw new Error('Failed to retrieve Statscore token');
        }

        const result = await cachedFetchJson(`https://api.statscore.com/v2/participants?token=${token}&season_id=${WC_2026_SEASON_ID}&type=team&sport_id=${SPORT_ID_FOOTBALL}`, TTL_PERMANENT);
        if (result?.api?.data?.participants) {
            return result.api.data.participants;
        } else {
            throw new Error('Invalid teams API structure');
        }
    } catch (error) {
        console.error('Error fetching World Cup teams:', error);
        throw error;
    }
}

export async function fetchWorldCupTeamSquad(teamId) {
    try {
        const token = await getStatscoreToken();
        if (!token) {
            throw new Error('Failed to retrieve Statscore token');
        }

        const result = await cachedFetchJson(`https://api.statscore.com/v2/participants/${teamId}/squad?token=${token}&season_id=${WC_2026_SEASON_ID}`, TTL_PERMANENT);
        if (result?.api?.data?.participants) {
            // Filter to include only athletes/players
            return result.api.data.participants.filter(p => p.details?.subtype === 'athlete');
        } else {
            throw new Error('Invalid squad API structure');
        }
    } catch (error) {
        console.error(`Error fetching squad for team ${teamId}:`, error);
        throw error;
    }
}

function buildGroupStandingsMap(standingsRes) {
    const groups = standingsRes?.api?.data?.standings?.groups ?? [];
    const map = {};
    for (const group of groups) {
        for (const participant of group.participants ?? []) {
            const col = (code) =>
                participant.columns?.find((c) => c.code === code)?.value ?? 0;
            map[participant.name] = {
                wins: col('won'),
                goalDiff: col('goals_diff'),
            };
        }
    }
    return map;
}

export async function fetchWorldCupFixtures() {
    const token = await getStatscoreToken();
    if (!token) {
        throw new Error('Failed to retrieve Statscore token');
    }

    const [standingsRes, ...fixtureResponses] = await Promise.all([
        cachedFetchJson(`https://api.statscore.com/v2/standings/${WC_2026_GROUP_STANDINGS_ID}?token=${token}`, TTL_PERMANENT),
        ...WC_2026_GROUP_IDS.map((groupId) =>
            cachedFetchJson(`https://api.statscore.com/v2/events?token=${token}&group_id=${groupId}&sport_id=${SPORT_ID_FOOTBALL}&season_id=${WC_2026_SEASON_ID}&events_details=yes`, TTL_PERMANENT)
        ),
    ]);

    const standingsMap = buildGroupStandingsMap(standingsRes);

    const allFixtures = fixtureResponses.flatMap((apiResponse) => {
        const groups = apiResponse?.api?.data?.competitions?.[0]?.seasons?.[0]?.stages?.[0]?.groups;
        return groups.flatMap((group) => {
            const groupName = group.name;
            return group.events.map((event) => {
                const [p1, p2] = event.participants;
                const [date, time] = event.start_date.split(' ');
                const t1 = p1?.name;
                const t2 = p2?.name;
                const t1Stats = standingsMap[t1] ?? { wins: 0, goalDiff: 0 };
                const t2Stats = standingsMap[t2] ?? { wins: 0, goalDiff: 0 };
                return {
                    groupName,
                    date,
                    time,
                    team1: t1,
                    team2: t2,
                    team1Wins: t1Stats.wins,
                    team2Wins: t2Stats.wins,
                    team1GoalDiff: t1Stats.goalDiff,
                    team2GoalDiff: t2Stats.goalDiff,
                    eventId: event.id,
                    statusType: event.status_type,
                    status: event.status,
                };
            });
        });
    });

    allFixtures.sort((a, b) => {
        const da = `${a.date} ${a.time}`;
        const db = `${b.date} ${b.time}`;
        return da < db ? -1 : da > db ? 1 : 0;
    });

    return allFixtures;
}

export async function fetchWorldCupGroupFixtures(groupId = 1) {
    try {
        const token = await getStatscoreToken();
        if (!token) {
            throw new Error('Failed to retrieve Statscore token');
        }

        const result = await cachedFetchJson(`https://api.statscore.com/v2/events?token=${token}&group_id=${groupId}&season_id=${WC_2026_SEASON_ID}&sport_id=${SPORT_ID_FOOTBALL}`, TTL_MED);
        const events = result?.api?.data?.competitions?.[0]?.seasons?.[0]?.stages?.[0]?.groups?.[0]?.events;
        if (events) {
            return events;
        } else {
            throw new Error('Invalid group fixtures API structure');
        }
    } catch (error) {
        console.error(`Error fetching group fixtures for group ${groupId}:`, error);
        throw error;
    }
}

export async function fetchWorldCupKnockoutEvents() {
    const token = await getStatscoreToken();
    if (!token) {
        throw new Error('Failed to retrieve Statscore token');
    }
    const result = await cachedFetchJson(`https://api.statscore.com/v2/events?token=${token}&stage_id=${WC_2026_STAGE_ID}&season_id=${WC_2026_SEASON_ID}&sport_id=${SPORT_ID_FOOTBALL}&events_details=yes`, TTL_PERMANENT);
    const groups = result?.api?.data?.competitions?.[0]?.seasons?.[0]?.stages?.[0]?.groups;
    return groups.flatMap(g => g.events);
}

export async function fetchGoldenBoot() {
    try {
        const token = await getStatscoreToken();
        if (!token) {
            throw new Error('Failed to retrieve Statscore token');
        }

        const result = await cachedFetchJson(`https://api.statscore.com/v2/standings/${WC_2026_GOLDEN_BOOT_STANDINGS_ID}?token=${token}&with_rows=yes`, TTL_PERMANENT);
        return result.api.data.standings.groups[0].participants;
    } catch (error) {
        throw error;
    }
}
